/**
 * Solar 발전소의 모든 Entity를 중앙에서 관리하는 Manager 클래스
 *
 * === ID 체계 ===
 * 모든 Entity는 PanelEntityId 값 객체로 식별된다.
 *
 *   entity.entityId.mapKey  — Map lookup key ("${type}_${seq}")
 *   entity.entityId.selfSeq — DB seq (객체 비교 기준)
 *
 * === 파라미터 규칙 ===
 * - entityId 파라미터 = 항상 entityId.mapKey (string) — Map lookup용
 * - selfSeq 파라미터 = 항상 DB seq (number) — API call용
 * - displayId 파라미터 = 항상 화면 표시 번호 (string) — 라벨용
 */
import { SolarEntity } from './SolarEntity.js';
import { PanelEntityId } from '@/features/panel-planning/utils/PanelEntityId';

export class SolarEntityManager {
  constructor() {
    /** @type {Map<string, SolarEntity>} — key = entityId.mapKey */
    this.entities = new Map();
    /** @type {SolarEntity[]} */
    this.rootEntities = [];
    /** @type {Set<string>} — key = entityId.mapKey */
    this.selectedEntities = new Set();
    /** @type {Map<string, Function[]>} */
    this.eventListeners = new Map();
  }

  /**
   * Entity 추가
   * @param {SolarEntity} entity
   * @returns {boolean}
   */
  addEntity(entity) {
    if (!entity || !entity.entityId) {
      console.error('❌ 유효하지 않은 Entity (entityId 없음):', entity);
      return false;
    }

    const key = entity.entityId.mapKey;
    if (this.entities.has(key)) {
      console.warn(`⚠️ Entity mapKey 중복: ${key}`);
      return false;
    }

    this.entities.set(key, entity);
    if (!entity.parentId) {
      this.rootEntities.push(entity);
    } else {
      const parent = this.entities.get(entity.parentId);
      if (parent) {
        parent.addChild(entity);
      }
    }
    this.emitEvent('entityAdded', entity);
    return true;
  }

  /**
   * Entity 수정 (metadata/children/id/parentId는 직접 수정 불가)
   * @param {string} entityMapKey — entity.entityId.mapKey
   * @param {Object} updates
   */
  updateEntity(entityMapKey, updates) {
    const entity = this.entities.get(entityMapKey);
    if (!entity) {
      console.error(`❌ Entity를 찾을 수 없음: ${entityMapKey}`);
      return false;
    }

    Object.keys(updates).forEach(key => {
      if (key === 'entityId' || key === 'id' || key === 'parentId') {
        console.warn(`⚠️ ${key}는 직접 수정할 수 없습니다. PanelEntityId는 불변입니다.`);
        return;
      }
      if (key === 'children') {
        console.warn('⚠️ children은 addChild/removeChild로 관리하세요.');
        return;
      }
      entity[key] = updates[key];
    });

    console.log(`✅ Entity 수정 완료: ${entity.getDisplayName()}`);
    this.emitEvent('entityUpdated', entity);
    return true;
  }

  /**
   * Entity 삭제
   * @param {string} entityMapKey — entity.entityId.mapKey
   * @param {boolean} recursive — 자식도 함께 삭제
   */
  deleteEntity(entityMapKey, recursive = true) {
    const entity = this.entities.get(entityMapKey);
    if (!entity) {
      console.error(`❌ Entity를 찾을 수 없음: ${entityMapKey}`);
      return false;
    }

    if (recursive && entity.children && entity.children.length > 0) {
      const childKeys = [...entity.children.map(child => child.entityId.mapKey)];
      childKeys.forEach(childKey => {
        this.deleteEntity(childKey, false);
      });
    }

    if (entity.parentId) {
      const parent = this.entities.get(entity.parentId);
      if (parent) {
        parent.removeChild(entityMapKey);
      }
    } else {
      const index = this.rootEntities.findIndex(e => e.entityId.mapKey === entityMapKey);
      if (index > -1) {
        this.rootEntities.splice(index, 1);
      }
    }

    this.selectedEntities.delete(entityMapKey);
    this.entities.delete(entityMapKey);

    console.log(`✅ Entity 삭제 완료: ${entity.getDisplayName()}`);
    this.emitEvent('entityDeleted', entity);
    return true;
  }

  /**
   * Entity 이동 (다른 부모로)
   * @param {string} entityMapKey — 이동할 Entity의 entityId.mapKey
   * @param {string|null} newParentMapKey — 새 부모 Entity의 entityId.mapKey
   */
  moveEntity(entityMapKey, newParentMapKey) {
    const entity = this.entities.get(entityMapKey);
    if (!entity) {
      console.error(`❌ Entity를 찾을 수 없음: ${entityMapKey}`);
      return false;
    }

    if (newParentMapKey && !this.entities.has(newParentMapKey)) {
      console.error(`❌ 새로운 부모 Entity를 찾을 수 없음: ${newParentMapKey}`);
      return false;
    }

    if (this.wouldCreateCycle(entityMapKey, newParentMapKey)) {
      console.error(`❌ 순환 참조: ${entityMapKey} -> ${newParentMapKey}`);
      return false;
    }

    // 기존 부모에서 제거
    if (entity.parentId) {
      const oldParent = this.entities.get(entity.parentId);
      if (oldParent) {
        oldParent.removeChild(entityMapKey);
      }
    } else {
      const index = this.rootEntities.findIndex(e => e.entityId.mapKey === entityMapKey);
      if (index > -1) {
        this.rootEntities.splice(index, 1);
      }
    }

    entity.parentId = newParentMapKey;

    if (newParentMapKey) {
      const newParent = this.entities.get(newParentMapKey);
      newParent.addChild(entity);
    } else {
      this.rootEntities.push(entity);
    }

    console.log(`✅ Entity 이동 완료: ${entity.getDisplayName()} -> ${newParentMapKey || 'root'}`);
    this.emitEvent('entityMoved', { entity, oldParentId: entity.parentId, newParentMapKey });
    return true;
  }

  /**
   * Entity 순서 재정렬
   * @param {string} parentMapKey — 부모 Entity의 entityId.mapKey
   * @param {string[]} newOrder — 자식 entityId.mapKey 순서 배열
   */
  reorderEntities(parentMapKey, newOrder) {
    const parent = this.entities.get(parentMapKey);
    if (!parent) {
      console.error(`❌ 부모 Entity를 찾을 수 없음: ${parentMapKey}`);
      return false;
    }
    if (!parent.children) {
      console.warn(`⚠️ 부모 Entity에 자식이 없음: ${parentMapKey}`);
      return false;
    }

    const reorderedChildren = [];
    newOrder.forEach(key => {
      const child = parent.children.find(c => c.entityId.mapKey === key);
      if (child) reorderedChildren.push(child);
    });

    parent.children = reorderedChildren;

    console.log(`✅ Entity 순서 재정렬 완료: ${parent.getDisplayName()}`);
    this.emitEvent('entitiesReordered', { parent, newOrder });
    return true;
  }

  /**
   * Entity 선택 (entityId는 mapKey 기준)
   * @param {string} entityMapKey — entity.entityId.mapKey
   * @param {'single'|'add'|'remove'} selectionType
   */
  selectEntity(entityMapKey, selectionType = 'single') {
    const entity = this.entities.get(entityMapKey);
    if (!entity) {
      console.error(`❌ Entity를 찾을 수 없음: ${entityMapKey}`);
      return false;
    }

    switch (selectionType) {
      case 'single':
        this.selectedEntities.forEach(prevKey => {
          const prev = this.entities.get(prevKey);
          if (prev) prev.setSelected(false);
        });
        this.selectedEntities.clear();
        this.selectedEntities.add(entityMapKey);
        entity.setSelected(true);
        break;

      case 'add':
        this.selectedEntities.add(entityMapKey);
        entity.setSelected(true);
        break;

      case 'remove':
        this.selectedEntities.delete(entityMapKey);
        entity.setSelected(false);
        break;

      default:
        console.warn(`⚠️ 알 수 없는 선택 타입: ${selectionType}`);
        return false;
    }

    this.emitEvent('entitySelectionChanged', {
      entity,
      selectionType,
      selectedKeys: Array.from(this.selectedEntities)
    });
    return true;
  }

  /** 전체 선택 해제 */
  clearSelection() {
    this.selectedEntities.forEach(key => {
      const entity = this.entities.get(key);
      if (entity) entity.setSelected(false);
    });
    this.selectedEntities.clear();
    this.emitEvent('selectionCleared');
  }

  /**
   * 타입별 Entity 찾기
   * @param {'array'|'string'|'module'} type
   * @returns {SolarEntity[]}
   */
  findEntitiesByType(type) {
    return Array.from(this.entities.values()).filter(
      entity => entity.entityId.entityType === type
    );
  }

  /**
   * 조건부 Entity 검색
   * @param {function(SolarEntity): boolean} predicate
   * @returns {SolarEntity[]}
   */
  findEntities(predicate) {
    return Array.from(this.entities.values()).filter(predicate);
  }

  /**
   * mapKey로 Entity 찾기
   * @param {string} entityMapKey — entity.entityId.mapKey
   * @returns {SolarEntity|null}
   */
  getEntity(entityMapKey) {
    return this.entities.get(entityMapKey) || null;
  }

  /**
   * selfSeq + entityType으로 Entity 찾기
   * @param {number} selfSeq — DB seq
   * @param {'array'|'string'|'module'} entityType
   * @returns {SolarEntity|null}
   */
  getEntityBySeq(selfSeq, entityType) {
    const key = `${entityType}_${selfSeq}`;
    return this.entities.get(key) || null;
  }

  /** @returns {SolarEntity[]} */
  getAllEntities() {
    return Array.from(this.entities.values());
  }

  /** @returns {SolarEntity[]} */
  getRootEntities() {
    return [...this.rootEntities];
  }

  /**
   * 선택된 Entity들
   * @returns {SolarEntity[]}
   */
  getSelectedEntities() {
    return Array.from(this.selectedEntities)
      .map(key => this.entities.get(key))
      .filter(Boolean);
  }

  /**
   * 순환 참조 검사
   * @param {string} entityMapKey
   * @param {string|null} newParentMapKey
   * @returns {boolean}
   */
  wouldCreateCycle(entityMapKey, newParentMapKey) {
    if (!newParentMapKey) return false;
    let current = newParentMapKey;
    while (current) {
      if (current === entityMapKey) return true;
      const parent = this.entities.get(current);
      if (!parent) break;
      current = parent.parentId;
    }
    return false;
  }

  // ── 이벤트 ──

  on(eventName, listener) {
    if (!this.eventListeners.has(eventName)) {
      this.eventListeners.set(eventName, []);
    }
    this.eventListeners.get(eventName).push(listener);
  }

  off(eventName, listener) {
    if (!this.eventListeners.has(eventName)) return;
    const listeners = this.eventListeners.get(eventName);
    const idx = listeners.indexOf(listener);
    if (idx > -1) listeners.splice(idx, 1);
  }

  emitEvent(eventName, data) {
    const listeners = this.eventListeners.get(eventName);
    if (!listeners) return;
    listeners.forEach(fn => {
      try { fn(data); } catch (e) { console.error(`❌ 이벤트 리스너 오류 (${eventName}):`, e); }
    });
  }

  // ── 초기화 ──

  /**
   * 평면화된 Entity 데이터 → EntityManager 초기화
   * @param {{ sections?: Array, arrays?: Array, strings?: Array, modules?: Array }} entityData
   */
  initializeFromEntityData(entityData) {
    console.log('🔄 SolarEntityManager 초기화 시작 (entityData)');
    this.entities.clear();
    this.rootEntities = [];
    this.selectedEntities.clear();
    const allEntities = [];

    // Section (있으면) → Array → String → Module
    for (const section of entityData.sections || []) {
      const sectionEntity = new SolarEntity({
        entityId: new PanelEntityId({
          selfSeq: section.id,
          entityType: 'array', // section은 array로 취급
          selfDisplayId: section.name,
          context: 'manager-init-entitydata'
        }),
        name: section.name,
      });
      allEntities.push(sectionEntity);

      for (const array of section.children || []) {
        const arrayEntity = new SolarEntity({
          entityId: new PanelEntityId({
            selfSeq: array.id,
            entityType: 'array',
            selfDisplayId: array.arrayId != null ? String(array.arrayId) : String(array.id),
            hierarchy: { rootSeq: array.id },
            context: 'manager-init-entitydata'
          }),
          name: array.name,
          parentId: sectionEntity.entityId.mapKey,
        });
        allEntities.push(arrayEntity);

        for (const str of array.children || []) {
          const strEntity = new SolarEntity({
            entityId: new PanelEntityId({
              selfSeq: str.id,
              entityType: 'string',
              selfDisplayId: str.stringId != null ? String(str.stringId) : null,
              hierarchy: {
                parentSeq: array.id,
                rootSeq: array.id
              },
              context: 'manager-init-entitydata'
            }),
            name: str.name,
            parentId: arrayEntity.entityId.mapKey,
          });
          allEntities.push(strEntity);

          for (const mod of str.children || []) {
            const modEntity = new SolarEntity({
              entityId: new PanelEntityId({
                selfSeq: mod.id,
                entityType: 'module',
                selfDisplayId: mod.moduleId != null ? String(mod.moduleId) : null,
                hierarchy: {
                  parentSeq: str.id,
                  parentDisplayId: str.stringId != null ? String(str.stringId) : null,
                  rootSeq: array.id
                },
                context: 'manager-init-entitydata'
              }),
              name: mod.moduleId != null ? String(mod.moduleId) : String(mod.id),
              parentId: strEntity.entityId.mapKey,
            });
            allEntities.push(modEntity);
          }
        }
      }
    }

    allEntities.forEach(e => this.addEntity(e));

    console.log('✅ SolarEntityManager 초기화 완료:', {
      totalEntities: this.entities.size,
      rootEntities: this.rootEntities.length,
    });
  }

  /**
   * 기존 solarStrings 데이터 초기화
   * @param {Array} solarStrings
   */
  initializeFromSolarStrings(solarStrings) {
    console.log('🔄 SolarEntityManager 초기화 시작 (solarStrings)');
    this.entities.clear();
    this.rootEntities = [];
    this.selectedEntities.clear();

    const result = SolarEntity.fromSolarStrings(solarStrings || []);
    result.entities.forEach(entity => this.addEntity(entity));
    this.rootEntities = result.rootEntities;

    console.log('✅ SolarEntityManager 초기화 완료:', {
      totalEntities: this.entities.size,
      rootEntities: this.rootEntities.length
    });
  }

  /** 디버깅 출력 */
  logStatus() {
    console.log('📊 SolarEntityManager 상태:');
    console.log(`- 총 Entity 수: ${this.entities.size}`);
    console.log(`- 루트 Entity 수: ${this.rootEntities.length}`);
    console.log(`- 선택된 Entity 수: ${this.selectedEntities.size}`);

    const typeCounts = {};
    this.entities.forEach(entity => {
      const t = entity.entityId.entityType;
      typeCounts[t] = (typeCounts[t] || 0) + 1;
    });
    console.log('- 타입별 개수:', typeCounts);
  }
}

export default SolarEntityManager;
